import React from 'react'
import styled, { css } from 'styled-components'
import classNames from 'classnames'
import { Badge as BadgeBs, BadgeProps as BadgePropsBs } from '@bootstrap-styled/v4'
import { badgeMixins, ifThen } from '@bootstrap-styled/css-mixins'
import Color from '@bootstrap-styled/color'
import { AppComponentStates, AppPaletteColors, AppBrandColors } from '../../typings/global'
import { omit } from 'ramda'

// type definition
export type BadgeProps = {
    strip?: boolean
    mark?: boolean
    property?: boolean
    state?: AppComponentStates | Extract<AppBrandColors, 'primary'>
    color?: BadgePropsBs['color'] | Extract<AppPaletteColors, 'green' | 'yellow'>
} & Omit<BadgePropsBs, 'color' | 'as' | 'property'>

const Badge = styled(
    (attrs: BadgeProps): JSX.Element => (
        <BadgeBs {...omit(['color', 'strip', 'mark', 'property', 'state'], attrs)} />
    )
).attrs(props => ({
    className: classNames(props.className, {
        'badge-strip': !!props.strip,
        'badge-mark': !!props.mark || !!props.property,
        [`badge-state-${props.state}`]: !!props.state,
        [`badge-${props.color}`]: !!props.color
    }),
    cssModule: {
        'badge-default':
            !!props.state ||
            (typeof props.color === 'undefined' || props.color === 'default'
                ? 'badge-default'
                : true),
        ...(props.cssModule || {})
    }
}))`
    && {
        display: inline-block;
        ${props =>
            ifThen(
                props.theme['$enable-rounded'],
                css`
                    border-radius: ${props.theme['$badge-border-radius']};
                `
            )}
    }

    & .badge-icon {
        width: auto;
        height: 0.7272727273em; /* if the badge's font size base was 11px this value would be 8px */
        display: inline-block;
        margin-right: ${props => props.theme['$spacers'][1]};

        &-right {
            margin-left: ${props => props.theme['$spacers'][1]};
        }
    }

    /* Colors */
    &.badge-default {
        color: ${props => props.theme['$gray']};
    }

    /* Custom colors */
    &.badge-green {
        ${props =>
            badgeMixins.badgeVariant(
                props.theme['$enable-hover-media-query'],
                props.theme['$green']
            )}
    }
    &.badge-yellow {
        ${props =>
            badgeMixins.badgeVariant(
                props.theme['$enable-hover-media-query'],
                props.theme['$yellow']
            )}
    }

    /* State colors */
    &.badge-state-active {
        color: ${props => props.theme['$green']};
        background-color: ${props =>
            Color(props.theme['$green'])
                .lighten(props.theme['$badge-strip-lighten-bg'])
                .string()};
    }
    &.badge-state-inactive {
        color: ${props => props.theme['$gray']};
        background-color: ${props =>
            Color(props.theme['$gray'])
                .lighten(props.theme['$badge-strip-lighten-bg'] * 1.15)
                .string()};
    }
    &.badge-state-created {
        color: ${props => props.theme['$purple']};
        background-color: ${props =>
            Color(props.theme['$purple'])
                .lighten(props.theme['$badge-strip-lighten-bg'] * 1.35)
                .string()};
    }
    &.badge-state-deleted {
        color: ${props => props.theme['$red']};
        background-color: ${props =>
            Color(props.theme['$red'])
                .lighten(props.theme['$badge-strip-lighten-bg'] * 0.85)
                .string()};
    }
    &.badge-state-pending {
        color: ${props => props.theme['$yellow']};
        background-color: ${props =>
            Color(props.theme['$yellow'])
                .lighten(props.theme['$badge-strip-lighten-bg'] * 0.65)
                .string()};
    }
    &.badge-state-primary {
        color: ${props => props.theme['$primary']};
        background-color: ${props =>
            Color(props.theme['$primary'])
                .lighten(props.theme['$badge-strip-lighten-bg'] * 0.9)
                .string()};
    }

    /* Types of badges */
    &&.badge-strip {
        font-size: inherit;
        border-radius: 0;
        padding: ${props =>
            `${props.theme['$badge-strip-padding-y']} ${props.theme['$badge-strip-padding-x']}`};
    }
    &&.badge-mark {
        max-width: 100%;
        text-align: left;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
`

export default Badge
